"use client";

import { Fragment, useState } from "react";
import { Combobox, Transition } from "@headlessui/react";
import Avatar from "boring-avatars";
import colors from "nice-color-palettes";

interface userList {
  id: string;
  handle: string | null;
  name: string | null;
  avatar: {
    colors: number;
    name: string;
    variant:
      | "marble"
      | "beam"
      | "pixel"
      | "sunset"
      | "ring"
      | "bauhaus"
      | undefined;
  } | null;
}

export default function FriendSelect({
  users,
  onChange,
  index,
  actor,
}: {
  users: userList[];
  onChange: (value: userList, index: number) => void;
  index: number;
  actor?: userList;
}) {
  const [selected, setSelected] = useState<userList | "">(actor ?? "");
  const [query, setQuery] = useState("");

  const handleSelection = (newValue: userList) => {
    setSelected(newValue);
    onChange(newValue, index);
  };

  const filteredUsers =
    query === ""
      ? users
      : users.filter((person) =>
          person
            .handle!.toLowerCase()
            .replace(/\s+/g, "")
            .includes(query.toLowerCase().replace(/\s+/g, "")),
        );

  return (
    <div className="">
      <Combobox value={selected} onChange={handleSelection}>
        <div className="relative">
          <div className="relative block rounded-md border border-gray-200 shadow-sm focus-within:border-slate-300 focus-within:ring-1 focus-within:ring-slate-300">
            <Combobox.Input
              className="border-none bg-transparent placeholder-transparent focus:border-transparent focus:outline-none focus:ring-0"
              displayValue={(person: {
                id: number;
                name: string;
                handle: string;
              }) => person.handle}
              onChange={(event) => setQuery(event.target.value)}
            />
            <span className="pointer-events-none absolute start-2.5 top-0 -translate-y-1/2 bg-white p-0.5 text-xs text-gray-700 transition-all peer-placeholder-shown:top-1/2 peer-placeholder-shown:text-sm peer-focus:top-0 peer-focus:text-xs">
              Actor
            </span>
            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="h-6 w-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M8.25 15 12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9"
                />
              </svg>
            </Combobox.Button>
          </div>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => setQuery("")}
          >
            <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full  overflow-y-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
              {filteredUsers.length === 0 && query !== "" ? (
                <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
                  Nothing found.
                </div>
              ) : (
                filteredUsers.map((person) => (
                  <Combobox.Option
                    key={person.id}
                    className={({ active }) =>
                      `relative cursor-default select-none py-2 pl-3 pr-4 ${
                        active ? "bg-gray-400 text-white" : "text-gray-900"
                      }`
                    }
                    value={person}
                  >
                    {({ selected, active }) => (
                      <div className="w-full">
                        <span
                          className={`flex items-center truncate   ${
                            selected ? "font-medium" : "font-normal"
                          }`}
                        >
                          <Avatar
                            colors={colors[person.avatar?.colors ?? 0]}
                            variant={person.avatar?.variant}
                            size={25}
                          />
                          <p className="ml-2 font-bold">{person.handle}</p>
                        </span>
                        {selected ? (
                          <span
                            className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                              active ? "text-white" : "text-teal-600"
                            }`}
                          ></span>
                        ) : null}
                      </div>
                    )}
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>
          </Transition>
        </div>
      </Combobox>
    </div>
  );
}
